/**
 * Workflow — §15 Trading lifecycle animation.  Horizontal on desktop
 * (10-node flow with moving packet), vertical stacked on mobile.
 */
import React from 'react'
import clsx from 'clsx'
import {
  ScanLine, BookmarkCheck, LineChart, Wand2, FlaskConical, ShieldCheck, Zap, Wallet, PieChart, RefreshCw, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { WORKFLOW } from '../data'
import { TONE } from '../_shared'
const ICONS = { ScanLine, BookmarkCheck, LineChart, Wand2, FlaskConical, ShieldCheck, Zap, Wallet, PieChart, RefreshCw }
export default function Workflow() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Trading Workflow</Eyebrow>
          <SectionHeading align="center"
            title={<>The complete <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">trading lifecycle.</span></>}
            subtitle="Ten discrete stages between discovering the trade and archiving the retro. Kalki Ai Cloud handles every stage on the same platform."
          />
        </div>

        {/* Desktop horizontal flow */}
        <div className="hidden lg:block relative">
          <svg viewBox="0 0 1400 220" className="w-full h-auto">
            <defs>
              <linearGradient id="wf-line" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor="#FFC107" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#22c55e" stopOpacity="0.4" />
              </linearGradient>
            </defs>
            <path id="wf-path" d="M 60 110 L 1340 110" stroke="url(#wf-line)" strokeWidth="2" strokeDasharray="4 8" fill="none" className="animate-brand-dash" />
            <circle r="4" fill="#FFC107">
              <animateMotion dur="12s" repeatCount="indefinite"><mpath href="#wf-path" /></animateMotion>
            </circle>
            {WORKFLOW.map((n, i) => {
              const x = 90 + (1220 * i) / (WORKFLOW.length - 1)
              const tone = TONE[n.tone] || TONE.orange
              const Icon = ICONS[n.iconKey] || Sparkles
              return (
                <g key={n.id} transform={`translate(${x} 110)`}>
                  <circle r="26" fill="rgba(10,10,12,0.9)" stroke={tone.hex} strokeOpacity="0.55" strokeWidth="1.4" />
                  <circle r="23" fill={`${tone.hex}12`} />
                  <foreignObject x="-12" y="-12" width="24" height="24">
                    <div className="flex items-center justify-center w-full h-full" style={{ color: tone.hex }}>
                      <Icon strokeWidth={1.8} style={{ width: 12, height: 12 }} />
                    </div>
                  </foreignObject>
                  <text y="46" textAnchor="middle" fontSize="9.5" fontWeight="600" fill="rgba(255,255,255,0.9)">{n.label}</text>
                  <text y="58" textAnchor="middle" fontSize="8" fontFamily="monospace" fill="rgba(255,255,255,0.35)">{String(i + 1).padStart(2, '0')}</text>
                </g>
              )
            })}
          </svg>
        </div>

        {/* Detail grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {WORKFLOW.map((n, i) => {
            const tone = TONE[n.tone] || TONE.orange
            const Icon = ICONS[n.iconKey] || Sparkles
            return (
              <div key={n.id} className="relative rounded-xl border border-white/10 bg-white/[0.02] p-3 overflow-hidden">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex items-center gap-2 mb-1">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon style={{ width: 11, height: 11 }} strokeWidth={2} />
                  </span>
                  <span className="text-[9.5px] font-mono uppercase tracking-wider" style={{ color: tone.hex }}>step {String(i + 1).padStart(2, '0')}</span>
                </div>
                <div className="relative text-[13px] font-semibold text-white leading-tight">{n.label}</div>
                <p className="relative text-[11.5px] text-zinc-400 leading-relaxed mt-1">{n.body}</p>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
